/**
 * The estate at a glance, laid out as the spine.
 *
 * Numbered sections that follow `docs/DESIGN.md` §2 in order — work arrives, the estate is what it
 * concerns, exposure is what is wrong, risk says what to do first. Someone reading top to bottom
 * has walked the product, which is the same reason `ai-security-ops` numbers its dashboard.
 */

import { Link } from 'react-router-dom';
import type { JSX } from 'react';

import { api } from '../shared/api/client';
import { Badge, Card, ErrorBox, Formula, PageHeader, Stat, StatRow, Table } from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';
import './overview.css';

export function Overview(): JSX.Element {
  const tickets = useAsync(() => api.tickets(100));
  const findings = useAsync(() => api.vulnerabilities(200));
  const board = useAsync(() => api.leaderboard(5));
  const collectors = useAsync(() => api.collectors());

  const open = findings.data ?? [];
  const cols = collectors.data ?? [];

  return (
    <>
      <PageHeader
        title="Estate overview"
        subtitle="work → estate → exposure → risk — the whole picture, in the order it matters"
      />

      <ErrorBox error={tickets.error ?? findings.error ?? collectors.error} />

      <Section
        number="01"
        title="Work"
        hint="what needs doing"
        to="/tickets"
        linkLabel="Tickets →"
      >
        <StatRow>
          <Stat label="Tickets" value={tickets.data?.items.length ?? '—'} />
          <Stat
            label="P1"
            value={tickets.data?.items.filter((t) => t.priority === 'p1').length ?? '—'}
            tone="danger"
          />
          <Stat
            label="Collectors reporting"
            value={cols.filter((c) => !c.is_stale && !c.revoked).length}
            hint={cols.some((c) => c.is_stale) ? 'some have gone quiet' : undefined}
            tone={cols.some((c) => c.is_stale) ? 'warn' : 'neutral'}
          />
        </StatRow>
      </Section>

      <Section
        number="02"
        title="Exposure"
        hint="what's wrong"
        to="/vulnerabilities"
        linkLabel="Vulnerabilities →"
      >
        <StatRow>
          <Stat label="Open findings" value={open.length} />
          <Stat
            label="Critical"
            value={open.filter((f) => f.severity === 'critical').length}
            tone="danger"
          />
          <Stat
            label="Known exploited"
            value={open.filter((f) => f.known_exploited).length}
            tone="danger"
            hint="observed in the wild"
          />
          <Stat
            label="Shadow assets"
            value={new Set(open.filter((f) => f.ci_id === null).map((f) => f.asset)).size}
            tone="warn"
            hint="scanner knows, CMDB does not"
          />
        </StatRow>
      </Section>

      <Section
        number="03"
        title="Risk"
        hint="how bad · what first"
        to="/leaderboard"
        linkLabel="Fix first →"
      >
        <Table columns={['CVE', 'Risk removed', 'Assets', '']} empty="Nothing to rank yet.">
          {(board.data ?? []).map((e) => (
            <tr key={e.cve}>
              <td className="table__strong">{e.cve}</td>
              <td className="table__num table__strong">{e.risk_removed_if_fixed}</td>
              <td className="table__num">{e.affected_assets}</td>
              <td>{e.known_exploited && <Badge tone="danger">exploited</Badge>}</td>
            </tr>
          ))}
        </Table>
      </Section>

      <Card
        title="Highest-risk finding"
        subtitle="Every score shows its working, so a ranking can be argued with rather than merely accepted."
      >
        {open.length === 0 ? (
          <p className="empty">No open findings. Import a scan to populate this.</p>
        ) : (
          <>
            <div className="table__strong">{open[0]?.title}</div>
            <div className="overview__sub">
              {open[0]?.asset} · {open[0]?.cve ?? 'no CVE'}
            </div>
            <Formula>{open[0]?.risk_explanation ?? ''}</Formula>
          </>
        )}
      </Card>
    </>
  );
}

function Section({
  number,
  title,
  hint,
  to,
  linkLabel,
  children,
}: {
  number: string;
  title: string;
  hint: string;
  to: string;
  linkLabel: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="ov">
      <div className="ov__rule" aria-hidden="true" />
      <div className="ov__body">
        <header className="ov__head">
          <span className="ov__number">{number}</span>
          <div>
            <div className="ov__title">{title}</div>
            <div className="ov__hint">{hint}</div>
          </div>
        </header>
        {children}
        <Link className="ov__link" to={to}>
          {linkLabel}
        </Link>
      </div>
    </section>
  );
}
